import { Box } from "@mui/material";
import React, { useMemo, useState } from "react";
import SecondaryTabMenuPicker from "../../../../common/SecondaryTabMenuPicker";
import {
  computeDifferences,
  getDailyDeviation,
  getMealDeviation,
} from "../../helpers/intakeHelpers";
import {
  calculateMealDeclarations,
  isMealFullySkipped,
} from "../../helpers/mealCalculationHelpers";
import {
  calculateAvailableMealTypes,
  findMealByType,
} from "../../helpers/planSelectors";
import { NutritionDailyType } from "../../model/nutritionPlanTypes";
import IntakeDeviationMessage from "./IntakeDeviationMessage";
import MacroRingWithLegend from "./MacroRingWithLegend";
import MealIntakeView from "./MealIntakeView";
import TargetComparison from "./TargetComparison";

const DAILY_TAB = 0;

type Props = {
  day: NutritionDailyType;
  /** Tab to open on: a meal type, or 0 for "Daily intake". */
  initialMealType?: number;
};

/**
 * The app's DayIntake screen in the drawer: a "Daily intake" + meals tab
 * strip that wraps onto further lines, every nutrient on the daily tab and
 * the per-meal segmented rings on a meal tab.
 */
const DayIntakePanel = ({ day, initialMealType }: Props) => {
  const [selectedTab, setSelectedTab] = useState<number>(
    initialMealType ?? DAILY_TAB
  );

  const mealTypes = useMemo(
    () => calculateAvailableMealTypes(day.meals),
    [day.meals]
  );
  const dailyOriginal = useMemo(
    () => computeDifferences(day.declaration, day.deviation ?? {}),
    [day.declaration, day.deviation]
  );
  const dailyDeviation = getDailyDeviation(day.deviation);

  const selectedMeal =
    selectedTab === DAILY_TAB ? undefined : findMealByType(day, selectedTab);
  const mealData = selectedMeal
    ? calculateMealDeclarations(selectedMeal)
    : null;

  const tabs = [
    { label: "Daily intake", key: DAILY_TAB },
    ...mealTypes.map((type) => ({ label: type.label, key: type.type })),
  ];
  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === selectedTab)
  );

  const dailyView = (
    <>
      <MacroRingWithLegend
        cals={day.declaration.cals}
        carbs={day.declaration.carbs}
        protein={day.declaration.protein}
        fat={day.declaration.fat}
      />
      <IntakeDeviationMessage
        deviationType={dailyDeviation.type ?? "increased"}
        show={dailyDeviation.hasDeviation}
      />
      <TargetComparison
        current={day.declaration}
        original={dailyOriginal}
        mode="all"
      />
    </>
  );

  return (
    <Box sx={styles.container}>
      <SecondaryTabMenuPicker
        tabs={tabs}
        selectedTab={selectedIndex}
        setSelectedTab={(index: number) => setSelectedTab(tabs[index].key)}
        isFullWidth={false}
        tabStyle={{ whiteSpace: "nowrap" }}
        wrap
      />

      {selectedMeal && mealData ? (
        <MealIntakeView
          mealDataCurrent={mealData.dataCurrent}
          mealDataOriginal={mealData.dataOriginal}
          dailyDataCurrent={day.declaration}
          isSkipped={isMealFullySkipped(selectedMeal)}
          allMeals={day.meals ?? []}
          currentMealType={selectedTab}
          deviation={getMealDeviation(
            mealData.dataCurrent.cals,
            mealData.dataOriginal.cals
          )}
        />
      ) : (
        dailyView
      )}
    </Box>
  );
};

export default DayIntakePanel;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
};
